/**
 * WMS Dashboard Widget Registry
 *
 * Defines all available widgets and their configurations.
 * Includes 30+ widgets across all categories.
 * hasBackend indicates widgets connected to DashboardController API endpoints.
 *
 * @author BizClick WMS
 * @version 3.0.0
 */

import { WidgetDefinition, WidgetCategory } from './types';

/**
 * All available widgets in the system
 */
export const WIDGET_REGISTRY: WidgetDefinition[] = [
  // === ORDERS ===
  {
    id: 'order-summary',
    name: 'Ordreoversikt',
    description: 'Sammendrag av ordrestatus med trender',
    category: 'orders',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'ClipboardList',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'orders-pending',
    name: 'Ventende ordrer',
    description: 'Antall ordrer som venter på behandling',
    category: 'orders',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 30,
    icon: 'Clock',
    minWidth: 2,
    minHeight: 2,
  },

  // === PICKING ===
  {
    id: 'picking-stats',
    name: 'Plukkstatistikk',
    description: 'Oversikt over plukkaktivitet og ytelse',
    category: 'picking',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide'],
    defaultRefreshInterval: 30,
    icon: 'PackageSearch',
    minWidth: 3,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'picking-progress',
    name: 'Plukkfremgang',
    description: 'Daglig plukkfremgang mot mål',
    category: 'picking',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 60,
    icon: 'Target',
    minWidth: 2,
    minHeight: 2,
  },
  {
    id: 'active-pickers',
    name: 'Aktive plukkere',
    description: 'Antall plukkere i aktivitet',
    category: 'picking',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 30,
    icon: 'Users',
    minWidth: 2,
    minHeight: 2,
  },

  // === RECEIVING ===
  {
    id: 'receiving-stats',
    name: 'Mottaksstatistikk',
    description: 'Oversikt over dagens mottak og forventede leveranser',
    category: 'receiving',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 60,
    icon: 'Truck',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'pending-putaway',
    name: 'Venter plassering',
    description: 'Varer mottatt som venter på plassering',
    category: 'receiving',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 60,
    icon: 'ArrowDownToLine',
    minWidth: 2,
    minHeight: 2,
  },

  // === INVENTORY ===
  {
    id: 'inventory-alerts',
    name: 'Lagervarsler',
    description: 'Kritiske varsler om lagerbeholdning',
    category: 'inventory',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'AlertTriangle',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },
  {
    id: 'warehouse-capacity',
    name: 'Kapasitet',
    description: 'Lagerfyllingsgrad per sone',
    category: 'inventory',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide'],
    defaultRefreshInterval: 300,
    icon: 'Warehouse',
    minWidth: 3,
    minHeight: 2,
    hasBackend: true,
  },

  // === SHIPPING ===
  {
    id: 'shipping-stats',
    name: 'Utsendingsstatistikk',
    description: 'Oversikt over utsendinger og klar-status',
    category: 'shipping',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Send',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'ready-to-ship',
    name: 'Klar til sending',
    description: 'Ordrer klare for utsending',
    category: 'shipping',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 30,
    icon: 'PackageCheck',
    minWidth: 2,
    minHeight: 2,
  },

  // === KPI ===
  {
    id: 'kpi-grid',
    name: 'KPI-oversikt',
    description: 'Nokkeltall for lagerytelse',
    category: 'kpi',
    defaultSize: 'wide',
    allowedSizes: ['large', 'wide'],
    defaultRefreshInterval: 60,
    icon: 'TrendingUp',
    minWidth: 4,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'pick-efficiency',
    name: 'Plukkeffektivitet',
    description: 'Plukkeffektivitet som prosent',
    category: 'kpi',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 60,
    icon: 'Gauge',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'on-time-delivery',
    name: 'Leveringspresisjon',
    description: 'Andel ordrer levert i tide',
    category: 'kpi',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 60,
    icon: 'Timer',
    minWidth: 2,
    minHeight: 2,
  },

  // === ACTIVITY ===
  {
    id: 'activity-feed',
    name: 'Aktivitetslogg',
    description: 'Sanntids aktivitetsstrøm fra lageret',
    category: 'activity',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 10,
    icon: 'Activity',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },
  {
    id: 'hourly-activity-chart',
    name: 'Timebasert aktivitet',
    description: 'Graf over aktivitet per time',
    category: 'activity',
    defaultSize: 'wide',
    allowedSizes: ['large', 'wide'],
    defaultRefreshInterval: 60,
    icon: 'BarChart3',
    minWidth: 4,
    minHeight: 2,
    hasBackend: true,
  },

  // === OPERATORS ===
  {
    id: 'operator-status',
    name: 'Operatorstatus',
    description: 'Oversikt over operatorer og deres oppgaver',
    category: 'operators',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 30,
    icon: 'Users',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },
  {
    id: 'top-performers',
    name: 'Topp-ytere',
    description: 'Operatorer med best ytelse i dag',
    category: 'operators',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 60,
    icon: 'Trophy',
    minWidth: 2,
    minHeight: 2,
  },

  // === SYSTEM ===
  {
    id: 'clock',
    name: 'Klokke',
    description: 'Dato og klokkeslett',
    category: 'system',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 0, // No API refresh, updates every second
    icon: 'Clock',
    minWidth: 2,
    minHeight: 2,
  },
  {
    id: 'weather',
    name: 'Vaer',
    description: 'Lokalt vaervarsel (for logistikkplanlegging)',
    category: 'system',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 300,
    icon: 'CloudSun',
    minWidth: 2,
    minHeight: 2,
  },

  // === KPI (new) ===
  {
    id: 'sparkline-kpi',
    name: 'KPI med Sparkline',
    description: 'Nokkeltall med mini trendgraf og endringsindikator',
    category: 'kpi',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 60,
    icon: 'TrendingUp',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'animated-counter',
    name: 'Animert teller',
    description: 'Stort animert tall - perfekt for TV-modus',
    category: 'kpi',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Hash',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'radial-gauge',
    name: 'Sirkeldiagram',
    description: 'Sirkulaer gauge for prosentverdier (effektivitet, utnyttelse)',
    category: 'kpi',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 60,
    icon: 'Gauge',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'pick-accuracy',
    name: 'Plukknoyaktighet',
    description: 'Prosent riktig plukk (plukket riktig vs. bestilt)',
    category: 'kpi',
    defaultSize: 'small',
    allowedSizes: ['small', 'medium'],
    defaultRefreshInterval: 60,
    icon: 'Target',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'daily-scorecard',
    name: 'Daglig resultatkort',
    description: 'Executive KPI-er med sirkeldiagrammer (OTIF, noyaktighet, utnyttelse)',
    category: 'kpi',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide'],
    defaultRefreshInterval: 60,
    icon: 'LayoutGrid',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === GAMIFICATION ===
  {
    id: 'goal-tracker',
    name: 'Malsporing',
    description: 'Daglige mal med fremgangsindikator og feiring ved 100%',
    category: 'gamification',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Flag',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'leaderboard',
    name: 'Toppliste',
    description: 'Topp plukkere i dag med podium (gull/solv/bronse)',
    category: 'gamification',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'Trophy',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === ALERTS ===
  {
    id: 'replenishment-alerts',
    name: 'Pafyllingsvarsler',
    description: 'Plukklokasjoner med lav beholdning som trenger pafylling',
    category: 'alerts',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'AlertTriangle',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },
  {
    id: 'bottleneck-detector',
    name: 'Flaskehalsdeteksjon',
    description: 'Identifiserer flaskehalser i lagerprosessene',
    category: 'alerts',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide'],
    defaultRefreshInterval: 60,
    icon: 'AlertOctagon',
    minWidth: 3,
    minHeight: 2,
    hasBackend: true,
  },

  // === RECEIVING (new) ===
  {
    id: 'incoming-deliveries',
    name: 'Forventede leveranser',
    description: 'Dagens forventede leveranser med nedtelling til ETA',
    category: 'receiving',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 120,
    icon: 'Truck',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === SHIPPING (new) ===
  {
    id: 'shipping-cutoff',
    name: 'Shipping cut-off',
    description: 'Nedtelling til neste shipping-deadline med klar-status',
    category: 'shipping',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Timer',
    minWidth: 2,
    minHeight: 2,
  },

  // === COMPARISON ===
  {
    id: 'comparison',
    name: 'I dag vs. i gar',
    description: 'Sammenligning av nokkeltall mellom i dag og i gar',
    category: 'comparison',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'ArrowLeftRight',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === CHARTS (Fase 3) ===
  {
    id: 'throughput-time',
    name: 'Gjennomlopstid',
    description: 'Gjennomsnittlig ordretid siste 7 dager med trendgraf',
    category: 'kpi',
    defaultSize: 'wide',
    allowedSizes: ['large', 'wide'],
    defaultRefreshInterval: 120,
    icon: 'Timer',
    minWidth: 4,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'order-flow',
    name: 'Ordreflyt',
    description: 'Traktvisning: Nye - Tildelt - Plukking - Pakket - Sendt',
    category: 'orders',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'GitBranch',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },
  {
    id: 'workload-forecast',
    name: 'Arbeidsmengde-prognose',
    description: 'Forventet arbeidsmengde resten av dagen med konfidensintervall',
    category: 'forecasting',
    defaultSize: 'wide',
    allowedSizes: ['large', 'wide'],
    defaultRefreshInterval: 300,
    icon: 'CloudLightning',
    minWidth: 4,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'mini-heatmap',
    name: 'Sone-heatmap',
    description: 'Rutenett som viser aktivitetsniva per lagersone',
    category: 'activity',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Grid3X3',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },

  // === ZONES & WORKFORCE (Fase 3) ===
  {
    id: 'zone-utilization',
    name: 'Soneutnyttelse',
    description: 'Lagerfyllingsgrad per sone med fargekoding',
    category: 'inventory',
    defaultSize: 'tall',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 300,
    icon: 'MapPin',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },
  {
    id: 'workforce-balance',
    name: 'Operatorfordeling',
    description: 'Fordeling av operatorer per oppgavetype',
    category: 'operators',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'Users',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'sla-monitor',
    name: 'SLA-overvaking',
    description: 'Leveringspresisjon per kunde med risikoindikatorer',
    category: 'kpi',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 120,
    icon: 'ShieldCheck',
    minWidth: 3,
    minHeight: 3,
    hasBackend: true,
  },

  // === COLLEAGUE DASHBOARD (Phase 5) ===
  {
    id: 'activity-log',
    name: 'Aktivitetslogg (plukk/mottak)',
    description: 'Time-for-time plukkelinjer, mottakslinjer og forsendelser med malstyring',
    category: 'activity',
    defaultSize: 'large',
    allowedSizes: ['large', 'wide', 'tall'],
    defaultRefreshInterval: 60,
    icon: 'BarChart3',
    minWidth: 4,
    minHeight: 4,
    hasBackend: true,
  },
  {
    id: 'remaining-orders',
    name: 'Gjenstaaende oppdrag',
    description: 'Plukkelinjer ikke pabegynt / pagar, gruppert per leveringsmetode',
    category: 'picking',
    defaultSize: 'medium',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 30,
    icon: 'ListTodo',
    minWidth: 3,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'reorder-point-alerts',
    name: 'Bestillingspunkt-varsler',
    description: 'Lokasjoner der beholdning er under beregnet bestillingspunkt',
    category: 'inventory',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'tall'],
    defaultRefreshInterval: 120,
    icon: 'AlertTriangle',
    minWidth: 4,
    minHeight: 3,
    hasBackend: true,
  },
  {
    id: 'stock-value',
    name: 'Lagerverdi',
    description: 'Total lagerverdi (innkjopspris x beholdning) i norsk valutaformat',
    category: 'kpi',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 300,
    icon: 'Banknote',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },
  {
    id: 'order-overview',
    name: 'Ordreoversikt (status)',
    description: 'Ordrer fordelt pa statusgrupper: Nye / Pagar / Til pakking / Fullforte',
    category: 'orders',
    defaultSize: 'medium',
    allowedSizes: ['small', 'medium', 'large'],
    defaultRefreshInterval: 30,
    icon: 'ClipboardList',
    minWidth: 2,
    minHeight: 2,
    hasBackend: true,
  },

  // === COMPASS (Spør Compass AI-rapporter) ===
  {
    id: 'compass-report',
    name: 'Compass Rapport',
    description: 'Vis en lagret AI-rapport fra Spør Compass',
    category: 'compass',
    defaultSize: 'large',
    allowedSizes: ['medium', 'large', 'wide', 'tall'],
    defaultRefreshInterval: 300,
    icon: 'Compass',
    minWidth: 3,
    minHeight: 2,
    hasBackend: true,
    allowMultiple: true,
  },
];

/**
 * Get widget definition by ID
 */
export const getWidgetById = (id: string): WidgetDefinition | undefined => {
  return WIDGET_REGISTRY.find((w) => w.id === id);
};

/**
 * Get widgets by category
 */
export const getWidgetsByCategory = (category: WidgetCategory): WidgetDefinition[] => {
  return WIDGET_REGISTRY.filter((w) => w.category === category);
};

/**
 * Get all categories with their widgets
 */
export const getWidgetsByCategories = (): Map<WidgetCategory, WidgetDefinition[]> => {
  const map = new Map<WidgetCategory, WidgetDefinition[]>();

  WIDGET_REGISTRY.forEach((widget) => {
    const existing = map.get(widget.category) || [];
    existing.push(widget);
    map.set(widget.category, existing);
  });

  return map;
};

/**
 * Default layout for new dashboards
 */
export const getDefaultLayout = () => ({
  id: 'default',
  name: 'Standard Dashboard',
  widgets: [
    // Top row - Key metrics
    {
      instanceId: 'inst-1',
      widgetId: 'clock',
      position: { x: 0, y: 0 },
      size: { width: 2, height: 2 },
      settings: { refreshInterval: 0, showHeader: true },
    },
    {
      instanceId: 'inst-2',
      widgetId: 'order-summary',
      position: { x: 2, y: 0 },
      size: { width: 3, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-3',
      widgetId: 'picking-stats',
      position: { x: 5, y: 0 },
      size: { width: 4, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-4',
      widgetId: 'shipping-stats',
      position: { x: 9, y: 0 },
      size: { width: 3, height: 2 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    // Middle row
    {
      instanceId: 'inst-5',
      widgetId: 'kpi-grid',
      position: { x: 0, y: 2 },
      size: { width: 6, height: 2 },
      settings: { refreshInterval: 60, showHeader: true },
    },
    {
      instanceId: 'inst-6',
      widgetId: 'hourly-activity-chart',
      position: { x: 6, y: 2 },
      size: { width: 6, height: 2 },
      settings: { refreshInterval: 60, showHeader: true },
    },
    // Bottom row
    {
      instanceId: 'inst-7',
      widgetId: 'operator-status',
      position: { x: 0, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 30, showHeader: true },
    },
    {
      instanceId: 'inst-8',
      widgetId: 'activity-feed',
      position: { x: 4, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 10, showHeader: true },
    },
    {
      instanceId: 'inst-9',
      widgetId: 'inventory-alerts',
      position: { x: 8, y: 4 },
      size: { width: 4, height: 4 },
      settings: { refreshInterval: 60, showHeader: true },
    },
  ],
  gridColumns: 12,
  gridRows: 8,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isDefault: true,
});
